/**
 * 招待 token を sessionStorage に一時保存する helper.
 *
 * 招待リンク (`/invite?token=xxx`) を踏んだ未認証ユーザーが、login / register を
 * 完了した後に元の招待 acceptance に戻れるよう token を引き継ぐ用途。
 *
 * PR-VERIFY-RESET-CLEANUP (L2 batch6 #3): 旧実装は format / TTL 検証ゼロで
 * 任意の文字列を sessionStorage に永続保管していた。改善:
 *   - 保存時に JSON で `{token, expiresAt}` の形にして TTL 7 日を付与
 *   - 取得時に format (32-128 char、英数記号 ASCII 限定) と TTL を検証、
 *     不正なら自動クリア
 *   - SSR / sessionStorage 不在環境 (iframe sandbox 等) でも例外を握り潰す
 */
const PENDING_INVITE_TOKEN_KEY = 'petacms_pending_invite_token';

const INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 招待 token の TTL (server 側と同一: members.ts INVITATION_TTL_MS)
const TOKEN_FORMAT_RE = /^[A-Za-z0-9_-]{16,256}$/;

interface StoredInviteToken {
  token: string;
  expiresAt: number;
}

function isStoredShape(v: unknown): v is StoredInviteToken {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { token: unknown }).token === 'string' &&
    typeof (v as { expiresAt: unknown }).expiresAt === 'number'
  );
}

export function getPendingInviteToken(): string | null {
  try {
    const raw = sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY);
    if (!raw) return null;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // 旧形式 (文字列のみ) との後方互換: format check 通れば採用、ダメなら破棄
      if (typeof raw === 'string' && TOKEN_FORMAT_RE.test(raw)) return raw;
      sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
      return null;
    }
    if (!isStoredShape(parsed)) {
      sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
      return null;
    }
    if (!TOKEN_FORMAT_RE.test(parsed.token)) {
      sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
      return null;
    }
    if (parsed.expiresAt < Date.now()) {
      sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
      return null;
    }
    return parsed.token;
  } catch {
    return null;
  }
}

export function setPendingInviteToken(token: string): void {
  try {
    if (!TOKEN_FORMAT_RE.test(token)) {
      // format 不正は保存自体せず、既存値もクリアしておく (汚染防止)
      sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
      return;
    }
    const stored: StoredInviteToken = {
      token,
      expiresAt: Date.now() + INVITE_TTL_MS,
    };
    sessionStorage.setItem(PENDING_INVITE_TOKEN_KEY, JSON.stringify(stored));
  } catch {
    // ignore
  }
}

export function clearPendingInviteToken(): void {
  try {
    sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
  } catch {
    // ignore
  }
}
